import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { CENTRAL_PHARMACY_PATHS } from '../pharmacyPaths';
import { PHARMACY_COPY } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock } from '../ui';
import SmartDistributionComposer from '../components/SmartDistributionComposer';

const copy = PHARMACY_COPY.DISTRIBUTIONS;
const EMPTY_FORM = { dest_site_id: '', notes: '', items: [] };

export default function PharmacyDistributionCreatePage() {
  const navigate = useNavigate();
  const { toast, centralSite, workSites } = usePharmacy();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const destSites = useMemo(
    () => workSites.filter((s) => !centralSite || String(s.id) !== String(centralSite.id)),
    [workSites, centralSite]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const medRes = await pharmacyService.listMedications({ status: 'ACTIVE' });
        if (cancelled) return;
        setMedications(medRes.success ? medRes.data || [] : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lineCount = useMemo(
    () =>
      form.items.filter((l) => l.medication_id && l.source_lot_id && l.quantity).length,
    [form.items]
  );
  const canSave = lineCount > 0 && form.dest_site_id && !busy;

  const goBack = () => navigate(CENTRAL_PHARMACY_PATHS.DISTRIBUTIONS);

  const submit = async () => {
    const items = form.items
      .filter((l) => l.medication_id && l.source_lot_id && l.quantity)
      .map((l) => ({
        medication_id: l.medication_id,
        source_lot_id: l.source_lot_id,
        quantity: l.quantity
      }))
      .reverse();
    if (!items.length) {
      toast('warning', copy.LINES_HINT);
      return;
    }
    if (items.some((l) => !l.source_lot_id)) {
      toast('warning', copy.LOT_REQUIRED);
      return;
    }
    if (!form.dest_site_id) {
      toast('warning', copy.DEST_REQUIRED);
      return;
    }
    setBusy(true);
    try {
      const res = await pharmacyService.createDistribution({
        dest_site_id: form.dest_site_id,
        notes: form.notes,
        items
      });
      if (res.success) {
        toast('success', copy.CREATE_OK.replace('{number}', res.data.distribution_number));
        navigate(CENTRAL_PHARMACY_PATHS.DISTRIBUTION_DETAIL(res.data.id), { replace: true });
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cp-order-page">
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={copy.CREATE_TITLE}
        description={copy.CREATE_DESC}
        actions={
          <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack}>
            <Icon name="chevronLeft" size={16} aria-hidden />
            {copy.BACK_TO_LIST}
          </button>
        }
      />

      {loading ? (
        <LoadingBlock label="Préparation du formulaire…" />
      ) : (
        <>
          <SmartDistributionComposer
            medications={medications}
            destSites={destSites}
            centralSiteId={centralSite?.id}
            value={form}
            onChange={setForm}
            copy={copy}
          />

          <div className="cp-order-page__actions">
            <div className="cp-order-page__actions-meta">
              <strong>{lineCount}</strong>
              <span>{copy.RESULTS_LINES}</span>
            </div>
            <div className="cp-order-page__actions-btns">
              <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack} disabled={busy}>
                {copy.CANCEL}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                onClick={submit}
                disabled={!canSave}
              >
                {busy ? copy.SAVING : copy.SUBMIT}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
